import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { getTeamLeads, saveTeamRoles } from '../../utils/api';
import { usePassLevels } from '../../hooks/usePassLevels';

export default function AddTeamModal({ open, onClose, onSave, showToast }) {
  const passLevels = usePassLevels();
  const [teamName, setTeamName]         = useState('');
  const [leadEmail, setLeadEmail]       = useState('');
  const [passLevel, setPassLevel]       = useState('');
  const [teamLeads, setTeamLeads]       = useState([]);
  const [excludeMeals, setExcludeMeals] = useState(false);
  const [rolesEnabled, setRolesEnabled] = useState(false);
  const [roles, setRoles]               = useState([]);
  const [newRole, setNewRole]           = useState('');
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (!open) return;
    getTeamLeads().then(d => setTeamLeads(d || [])).catch(console.error);
  }, [open]);

  function reset() {
    setTeamName(''); setLeadEmail(''); setPassLevel('SL');
    setExcludeMeals(false); setRolesEnabled(false); setRoles([]); setNewRole('');
  }

  function addRole() {
    const trimmed = newRole.trim();
    if (!trimmed || roles.includes(trimmed)) return;
    setRoles(prev => [...prev, trimmed]);
    setNewRole('');
  }

  function removeRole(role) {
    setRoles(prev => prev.filter(r => r !== role));
  }

  async function handleSave() {
    if (!teamName.trim()) { showToast('Team name is required.'); return; }
    setSaving(true);
    try {
      const saved = await onSave({ name: teamName.trim(), leadEmail, passLevel, excludeMeals, colorClass: '', notes: '' });
      // Save roles using the team id returned from the server
      const teamId = saved?.id || teamName.trim();
      await saveTeamRoles(teamId, rolesEnabled, roles);
      reset();
      onClose();
    } catch (err) {
      showToast('Error saving team.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Add Team"
      footer={
        <>
          <button className="btn" onClick={() => { reset(); onClose(); }}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Add Team'}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">Team Name</label>
        <input className="form-input" value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="e.g. Hospitality" />
      </div>
      <div className="form-group">
        <label className="form-label">Team Lead</label>
        <select className="form-input" value={leadEmail} onChange={e => setLeadEmail(e.target.value)}>
          <option value="">None assigned</option>
          {teamLeads.map(tl => (
            <option key={tl.email} value={tl.email}>{tl.name || tl.email}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Default Pass Level</label>
        <select className="form-input" value={passLevel || passLevels[0] || ''} onChange={e => setPassLevel(e.target.value)}>
          {passLevels.map(pl => <option key={pl} value={pl}>{pl}</option>)}
        </select>
      </div>

      <div className="form-group" style={{ marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={excludeMeals}
            onChange={e => setExcludeMeals(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Exclude from meal blocks
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              This team's roster won't count toward meal totals (e.g. teams that serve meals).
            </div>
          </div>
        </label>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={rolesEnabled}
            onChange={e => setRolesEnabled(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Enable sub-roles for this team
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              Team leads will see a separate roster tab per role in the sidebar.
            </div>
          </div>
        </label>

        {rolesEnabled && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Roles</div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 8 }}>
              {roles.length === 0 && (
                <div style={{ padding: '12px 14px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
                  No roles yet — add one below.
                </div>
              )}
              {roles.map((role, i) => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderBottom: i < roles.length - 1 ? '1px solid var(--border)' : 'none', background: 'var(--off)' }}>
                  <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{role}</span>
                  <button className="btn-ghost btn-xs" onClick={() => removeRole(role)} style={{ color: 'var(--danger, #c00)', fontSize: 12 }}>Remove</button>
                </div>
              ))}
              <div style={{ padding: 10, background: 'var(--off)', borderTop: roles.length > 0 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Role name, e.g. Concourse…"
                    value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRole()}
                    style={{ flex: 1, fontSize: 13 }}
                  />
                  <button className="btn btn-sm btn-dark" onClick={addRole} disabled={!newRole.trim()}>Add</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
